/**
 * Earned Income Tax Credit estimator (BUILD-SPEC.md §4.2). Computes the credit
 * from the published phase-in rate, plateau, and phase-out by number of
 * qualifying children — cited to the IRS revenue procedure. A refundable credit:
 * it can pay out even with no tax owed.
 */
import { Money } from "../engine/money";
import { estimateEitc } from "../engine/benefits";
import { el } from "../ui/dom";
import { field, parseNonNegative, tryExampleButton } from "../ui/form";
import { resultCard, type BreakdownLine } from "../ui/resultCard";
import { filesSeparately, marriedCheckbox, marriedDefault } from "./owedShared";
import type { SituationStore } from "../profile/situation";
import { EITC_JOINT_RETURN_CITATION } from "../data/statutes";
import type { TileContext, TileDefinition } from "./types";

interface Fields {
  earnedIncome: number;
  qualifyingChildren: number;
  married: boolean;
  /** Aggregate investment income, the §32(i) cutoff's input. */
  investmentIncome: number;
}

const EXAMPLE: Fields = {
  earnedIncome: 30000,
  qualifyingChildren: 1,
  married: false,
  investmentIncome: 0,
};

function readFields(p: URLSearchParams, profile: SituationStore): Fields {
  return {
    earnedIncome: p.has("inc")
      ? parseNonNegative(p.get("inc"), 0)
      : (profile.get("annualIncome") ?? 0),
    qualifyingChildren: p.has("kids")
      ? Math.max(0, parseNonNegative(p.get("kids"), 0))
      : (profile.get("qualifyingChildren") ?? 0),
    married: p.has("mfj") ? p.get("mfj") === "1" : marriedDefault(profile),
    investmentIncome: parseNonNegative(p.get("inv"), 0),
  };
}

function writeFields(f: Fields): URLSearchParams {
  const p = new URLSearchParams();
  p.set("inc", String(f.earnedIncome));
  p.set("kids", String(f.qualifyingChildren));
  // Written whether or not the box is ticked. A link is the sender's answer,
  // and "not a joint return" is an answer: dropping it lets the READER's My
  // Situation answer instead, so the same link opens joint for a married
  // reader and single for everyone else.
  p.set("mfj", f.married ? "1" : "0");
  p.set("inv", String(f.investmentIncome));
  return p;
}

export function mountEitc(ctx: TileContext): void {
  const { root, data, profile } = ctx;
  root.replaceChildren();
  const maybeData = data?.eitcCtc() ?? null;
  if (!maybeData) {
    root.append(
      el("div", {
        class: "verify-banner",
        attrs: { role: "alert" },
        text: "EITC data is unavailable, verify before relying on any figure.",
      }),
    );
    return;
  }
  // Capture the narrowed (non-null) dataset so the nested closures keep the type.
  const eitcCtc = maybeData;
  let fields = readFields(ctx.params, profile);

  const incInput = el("input", {
    type: "number",
    name: "inc",
    min: 0,
    step: 1000,
    value: fields.earnedIncome,
    attrs: { "aria-label": "Earned income", inputmode: "decimal" },
  });
  const kidsInput = el("input", {
    type: "number",
    name: "kids",
    min: 0,
    step: 1,
    value: fields.qualifyingChildren,
    attrs: { "aria-label": "Qualifying children", inputmode: "numeric" },
  });
  const invInput = el("input", {
    type: "number",
    name: "inv",
    min: 0,
    step: 100,
    value: fields.investmentIncome,
    attrs: { "aria-label": "Investment income", inputmode: "decimal" },
  });
  const mfj = marriedCheckbox(fields.married);

  const resultContainer = el("div", { class: "tile-result", attrs: { "aria-live": "polite" } });

  function compute(): void {
    const r = estimateEitc(
      {
        earnedIncome: fields.earnedIncome,
        qualifyingChildren: fields.qualifyingChildren,
        married: fields.married,
        investmentIncome: fields.investmentIncome,
      },
      eitcCtc,
    );
    const fmt = (m: Money): string => m.format(ctx.locale);
    const lines: BreakdownLine[] = [
      {
        label: "Qualifying children",
        value: `${r.qualifyingChildren}${fields.qualifyingChildren > 3 ? " (capped at 3+)" : ""}`,
      },
      {
        label: "Investment income limit",
        value: `${fmt(r.investmentIncomeLimit)} — above it the credit is gone entirely.`,
        citation: eitcCtc.citation,
      },
      { label: "Estimated EITC", value: fmt(r.credit), emphasis: true, citation: eitcCtc.citation },
      {
        label: "Note",
        value: r.disqualifiedByInvestmentIncome
          ? `Investment income over ${fmt(r.investmentIncomeLimit)} ends the credit outright, whatever you earn.`
          : r.phasedOut
            ? "Income is past the phase-out, no credit at this level."
            : "A refundable credit. Eligibility also depends on the qualifying-child tests and (for no children) age 25–64.",
      },
    ];
    // My Situation says married filing separately, and the schedule above has no
    // column for that: §32(d)(1) applies the credit only to a joint return. The
    // figure is left standing rather than zeroed, because §32(d)(2)(B) really
    // does reach a separated spouse who lived with a qualifying child, and that
    // turns on facts this site does not hold.
    if (filesSeparately(fields.married, profile)) {
      lines.push({
        label: "Filing separately",
        value:
          "My Situation says married filing separately. The credit generally requires a joint " +
          "return — unless you lived apart from your spouse and with a qualifying child, which " +
          "only you can say. Treat the figure above as conditional on that.",
        citation: EITC_JOINT_RETURN_CITATION,
      });
    }

    resultContainer.replaceChildren(
      resultCard({
        label: "Estimated Earned Income Tax Credit",
        value: r.credit,
        locale: ctx.locale,
        breakdown: lines,
        permalink: () => ctx.permalink(writeFields(fields)),
      }),
    );
  }

  function recompute(): void {
    fields = {
      earnedIncome: parseNonNegative(incInput.value, 0),
      qualifyingChildren: Math.max(0, parseNonNegative(kidsInput.value, 0)),
      married: mfj.checked,
      investmentIncome: parseNonNegative(invInput.value, 0),
    };
    ctx.setParams(writeFields(fields));
    profile.set("annualIncome", fields.earnedIncome);
    profile.set("qualifyingChildren", fields.qualifyingChildren);
    compute();
  }

  mfj.addEventListener("change", recompute);
  for (const i of [incInput, kidsInput, invInput]) i.addEventListener("input", recompute);

  const tryExample = tryExampleButton(() => {
    fields = { ...EXAMPLE };
    incInput.value = String(fields.earnedIncome);
    kidsInput.value = String(fields.qualifyingChildren);
    invInput.value = String(fields.investmentIncome);
    mfj.checked = fields.married;
    recompute();
  });

  const form = el(
    "form",
    { class: "tile-form", on: { submit: (e) => e.preventDefault() } },
    field("Earned income", incInput),
    field("Qualifying children", kidsInput),
    field("Investment income (interest, dividends, capital gains)", invInput),
    el("label", { class: "checkbox" }, mfj, el("span", { text: "Married filing jointly" })),
    el("div", { class: "tile-form-actions" }, tryExample),
  );

  root.append(form, resultContainer);
  compute();
}

export const eitcTile: TileDefinition = {
  id: "eitc",
  title: "Earned Income Tax Credit",
  pillar: "owed",
  description: "EITC from the published phase-in and phase-out.",
  keywords: ["eitc", "earned income", "credit", "refundable"],
  status: "ready",
  how: "The Earned Income Tax Credit phases in as a percentage of your earned income, rises to a maximum, holds on a plateau, then phases out above an income threshold. The rates, maximum, and thresholds all depend on your number of qualifying children and whether you file jointly. We use the published 2026 figures.\n\nIt's refundable, it can pay out even if you owe no tax. One rule is a cliff rather than a curve: §32(i) allows no credit at all once your aggregate investment income — interest, dividends, capital gains, rents and royalties — passes the year's limit, whatever you earned, so that figure is a field here. Real eligibility also depends on the qualifying-child tests and, for filers with no children, being age 25–64.",
  resources: [
    {
      label: "IRS, Earned Income Tax Credit",
      url: "https://www.irs.gov/credits-deductions/individuals/earned-income-tax-credit-eitc",
    },
    {
      label: "IRS Publication 596 (EITC)",
      url: "https://www.irs.gov/forms-pubs/about-publication-596",
    },
  ],
  mount: mountEitc,
};
